import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import {
  BarChart2, Users, Clock, X, FileText, HelpCircle, 
  Calendar, Activity, CheckCircle, Laptop, RefreshCcw,
  AlertCircle, Loader2, Search, Mail, Calendar as CalendarIcon,
  Hash, Type, Star, ToggleLeft
} from 'lucide-react';

// Utility functions
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return 'Invalid Date';
  }
};

const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return 'Invalid Time';
  }
};

const formatDeviceInfo = (deviceInfo) => {
  if (!deviceInfo) return 'Unknown Device';
  try {
    const browser = deviceInfo.match(/Chrome|Firefox|Safari|Edge|IE/i)?.[0] || 'Unknown Browser';
    const os = deviceInfo.match(/Windows|Mac|Linux|Android|iOS/i)?.[0] || 'Unknown OS';
    return `${browser} on ${os}`;
  } catch (e) {
    return 'Unknown Device';
  }
};

const calculateTimeSpent = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return Math.max(0, Math.round((end - start) / (1000 * 60))); // Convert to minutes
  } catch (e) {
    return 0;
  }
};

// Custom Components
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center h-64">
    <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
    <p className="text-gray-500">Loading analytics data...</p>
  </div>
);

const NoDataMessage = ({ message, icon: Icon = HelpCircle }) => (
  <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg">
    <Icon className="h-12 w-12 text-gray-400 mb-4" />
    <p className="text-gray-600 font-medium">{message || 'No data available'}</p>
  </div>
);

const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
    <div className="flex justify-between items-center">
      <div className={`p-2 rounded-lg bg-${color}-50`}>
        <Icon className={`h-6 w-6 text-${color}-600`} />
      </div>
    </div>
    <h3 className="mt-4 text-2xl font-bold text-gray-900">{value}</h3>
    <p className="mt-1 text-sm text-gray-500">{title}</p>
  </div>
);

const getQuestionIcon = (type) => {
  switch (type) {
    case 'short_text':
    case 'long_text':
      return Type;
    case 'rating':
      return Star;
    case 'yes_no':
      return ToggleLeft;
    case 'email':
      return Mail;
    case 'date':
      return CalendarIcon;
    case 'number':
      return Hash;
    case 'single_choice':
    case 'multiple_choice':
      return FileText;
    case 'linear_scale':
      return Star;
    default:
      return FileText;
  }
};

const QuestionCard = ({ question, responses, allResponses }) => {
  const Icon = getQuestionIcon(question.type);

  const renderMetrics = () => {
    switch (question.type) {
      case 'single_choice':
      case 'multiple_choice': {
        // Get actual selected options from responses
        const actualResponses = allResponses?.map(response => {
          const answer = response.answers.find(a => a.question === question.id);
          return answer?.selectedOptions?.length > 0 
            ? answer.selectedOptions 
            : [answer?.answerText]
        }).flat().filter(Boolean);

        // Calculate actual counts and percentages
        const options = responses.optionCounts.map(opt => {
          const count = actualResponses?.filter(response => response === opt.option).length || 0;
          return {
            option: opt.option,
            count,
            percentage: responses.total > 0 ? (count / responses.total) * 100 : 0
          };
        });

        return options.length > 0 ? (
          <div className="space-y-4">
            {options.map((opt, idx) => (
              <div key={idx} className="relative">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{opt.option}</span>
                  <span className="text-gray-500">
                    {opt.count} ({Math.round(opt.percentage)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-blue-500 rounded-full transition-all duration-500"
                    style={{ width: `${opt.percentage || 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <NoDataMessage message="No responses yet" />
        );
      }

      case 'linear_scale':
      case 'rating': {
        // Find actual answers from responses array
        const actualAnswers = allResponses?.map(response => {
          const answer = response.answers.find(a => a.question === question.id);
          return answer?.scaleValue || null;
        }).filter(value => value !== null);

        const averageValue = actualAnswers.length > 0 
          ? actualAnswers.reduce((sum, val) => sum + val, 0) / actualAnswers.length
          : 0;

        // Create distribution data
        const distribution = {};
        actualAnswers.forEach(value => {
          distribution[value] = (distribution[value] || 0) + 1;
        });

        const distributionData = Object.entries(distribution).map(([value, count]) => ({
          value: parseInt(value),
          count,
          percentage: (count / actualAnswers.length) * 100
        })).sort((a, b) => a.value - b.value);

        return (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {averageValue ? averageValue.toFixed(1) : 'N/A'}
                </div>
                <div className="text-sm text-gray-500">Average Rating</div>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {responses.total || 0}
                </div>
                <div className="text-sm text-gray-500">Total Responses</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {Math.max(...actualAnswers) || 'N/A'}
                </div>
                <div className="text-sm text-gray-500">Highest Rating</div>
              </div>
            </div>
            
            {distributionData.length > 0 && (
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={distributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="value" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      }

      case 'short_text':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-bold">
                  {responses.answeredCount || 0}
                </div>
                <div className="text-sm text-gray-500">Total Responses</div>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {responses.averageLength || 0}
                </div>
                <div className="text-sm text-gray-500">Average Length</div>
              </div>
            </div>
            {responses.commonWords?.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">
                  Common Responses
                </h5>
                <div className="flex flex-wrap gap-2">
                  {responses.commonWords.map((word, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                    >
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <NoDataMessage 
            message="No analytics available for this question type"
            icon={AlertCircle}
          />
        );
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50">
            <Icon className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h4 className="text-lg font-medium text-gray-900">{question.text}</h4>
            <p className="text-sm text-gray-500">
              {responses.total || 0} responses
            </p>
          </div>
        </div>
        <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm capitalize">
          {question.type.replace(/_/g, ' ')}
        </span>
      </div>
      {renderMetrics()}
    </div>
  );
};

const ResponseDetailsModal = ({ response, onClose, survey }) => {
  if (!response) return null;

  const timeSpent = calculateTimeSpent(
    response.metadata?.startedAt,
    response.metadata?.completedAt
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Response Details</h3>
              <p className="text-sm text-gray-500">
                Submitted on {formatDate(response.createdAt)} at {formatTime(response.createdAt)}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              Respondent Information
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">
                  {response.respondent?.name || 'Anonymous'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">
                  {response.respondent?.email || 'N/A'}
                </p>
              </div>
              {response.respondent?.graduationYear && (
                <div>
                  <p className="text-sm text-gray-500">Graduation Year</p>
                  <p className="font-medium">{response.respondent.graduationYear}</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {response.answers.map((answer, index) => {
              // Find the corresponding question from survey questions
              const question = survey.questions?.find(q => q.question?.id === answer.question);
              if (!question) return null;

              return (
                <div key={index} className="border-b border-gray-200 pb-6">
                  <p className="text-sm text-gray-500 mb-1">Question {index + 1}</p>
                  <p className="font-medium text-gray-900 mb-2">{question.question.text}</p>
                  
                  {(() => {
                    switch (question.question.type) {
                      case 'multiple_choice':
                        return (
                          <div className="space-y-1">
                            {answer.selectedOptions?.map((option, idx) => (
                              <div key={idx} className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>{option}</span>
                              </div>
                            ))}
                          </div>
                        );

                      case 'rating':
                      case 'linear_scale':
                        return (
                          <div className="flex items-center space-x-2">
                            <span className="text-xl font-medium text-blue-600">
                              {answer.scaleValue || 'N/A'}
                            </span>
                          </div>
                        );

                      case 'single_choice':
                        return (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span>{answer.answerText}</span>
                          </div>
                        );

                      default:
                        return (
                          <p className="text-gray-700">
                            {answer.answerText || 'No response'}
                          </p>
                        );
                    }
                  })()}
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Response Metadata</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Device Info</p>
                <p className="font-medium">{formatDeviceInfo(response.metadata?.deviceInfo)}</p>
              </div>
              <div>
                <p className="text-gray-500">Time Spent</p>
                <p className="font-medium">{timeSpent} minutes</p>
              </div>
              <div>
                <p className="text-gray-500">Started At</p>
                <p className="font-medium">
                  {formatDate(response.metadata?.startedAt)} {formatTime(response.metadata?.startedAt)}
                </p>
              </div>
              <div>
              <p className="text-gray-500">Completed At</p>
                  <p className="font-medium">
                    {formatDate(response.metadata?.completedAt)} {formatTime(response.metadata?.completedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
 
  );
};

const SurveyAnalyticsModal = ({ isOpen, onClose, survey }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeRange, setTimeRange] = useState(7);

  useEffect(() => {
    if (isOpen) {
      fetchAnalytics();
    }
  }, [isOpen]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/surveys/surveys/${survey._id}/analytics?range=${timeRange}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch analytics');

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const filteredResponses = useMemo(() => {
    if (!analytics?.responses) return [];
    
    return analytics.responses.filter(response => {
      const searchLower = searchTerm.toLowerCase();
      return (
        response.respondent?.name?.toLowerCase().includes(searchLower) ||
        response.respondent?.email?.toLowerCase().includes(searchLower) ||
        String(response.respondent?.graduationYear).includes(searchLower)
      );
    });
  }, [analytics?.responses, searchTerm]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'questions', label: 'Questions', icon: FileText },
    { id: 'responses', label: 'Responses', icon: Users },
    { id: 'timeline', label: 'Timeline', icon: Calendar }
  ];

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading) return <LoadingSpinner />;
    if (error) {
      return (
        <div className="bg-red-50 rounded-lg p-6 flex flex-col items-center">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Try Again
          </button>
        </div>
      );
    }
    if (!analytics) return <NoDataMessage message="No analytics data available" />;

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Responses"
                value={analytics.overview.totalResponses}
                icon={Users}
              />
              <StatCard
                title="Completion Rate"
                value={`${analytics.overview.completionRate}%`}
                icon={CheckCircle}
                color="green"
              />
              <StatCard
                title="Avg. Time Spent"
                value={`${analytics.overview.averageTimeSpent} min`}
                icon={Clock}
                color="purple"
              />
              <StatCard
                title="Device Coverage"
                value={analytics.engagement.deviceBreakdown?.[0]?.name || 'N/A'}
                icon={Laptop}
                color="orange"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Device Distribution */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Device Distribution
                </h3>
                {analytics.engagement.deviceBreakdown?.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analytics.engagement.deviceBreakdown}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percentage }) => `${name} (${percentage}%)`}
                        >
                          {analytics.engagement.deviceBreakdown.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <NoDataMessage message="No device data available" />
                )}
              </div>

              {/* Response Timeline */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Response Timeline
                </h3>
                {analytics.timeline.responsesByDay?.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analytics.timeline.responsesByDay}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date"
                          tickFormatter={formatDate}
                        />
                        <YAxis />
                        <Tooltip 
                          labelFormatter={formatDate}
                          formatter={(value) => [value, 'Responses']}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="responses" 
                          stroke="#3B82F6"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <NoDataMessage message="No timeline data available" />
                )}
              </div>
            </div>
          </div>
        );

      case 'questions':
        return (
          <div className="space-y-6">
            {analytics.questions?.map((question, index) => (
              <QuestionCard
                key={question.question.id || index}
                question={question.question}
                responses={question.responses}
                allResponses={analytics.responses} // Add this prop

              />
            ))}
          </div>
        );

      case 'responses':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Individual Responses</h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      placeholder="Search respondents..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {filteredResponses.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Respondent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Submitted
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Time Spent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredResponses.map((response) => {
                        const timeSpent = calculateTimeSpent(
                          response.metadata?.startedAt,
                          response.metadata?.completedAt
                        );

                        return (
                          <tr key={response._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-10 w-10 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                                  <span className="text-blue-600 font-medium">
                                    {response.respondent?.name?.[0] || '?'}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {response.respondent?.name || 'Anonymous'}
                                  </div>
                                 
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {formatDate(response.createdAt)}
                              </div>
                              <div className="text-sm text-gray-500">
                                {formatTime(response.createdAt)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {timeSpent} minutes
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                                ${response.isComplete
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'}`}
                              >
                                {response.isComplete ? 'Complete' : 'Partial'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => setSelectedResponse(response)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6">
                  <NoDataMessage 
                    message={searchTerm ? "No matching responses found" : "No responses yet"} 
                    icon={searchTerm ? Search : Users}
                  />
                </div>
              )}
            </div>

            {selectedResponse && (
              <ResponseDetailsModal
                response={selectedResponse}
                onClose={() => setSelectedResponse(null)}
                survey={analytics}
              />
            )}
          </div>
        );

      case 'timeline':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-blue-600 font-medium">Peak Response Day</div>
                <div className="text-lg font-bold">
                  {formatDate(analytics.timeline.responsesByDay.reduce(
                    (max, day) => day.responses > max.responses ? day : max,
                    { responses: -1 }
                  ).date)}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-green-600 font-medium">Peak Hour</div>
                <div className="text-lg font-bold">
                  {analytics.timeline.responsesByHour.reduce(
                    (max, hour) => hour.count > max.count ? hour : max,
                    { count: -1 }
                  ).hour}:00
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-sm text-purple-600 font-medium">Active Days</div>
                <div className="text-lg font-bold">
                  {analytics.timeline.responsesByDay.filter(day => day.responses > 0).length}
                </div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="text-sm text-orange-600 font-medium">Total Responses</div>
                <div className="text-lg font-bold">
                  {analytics.overview.totalResponses}
                </div>
              </div>
            </div>

            {/* Daily Response Pattern */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Daily Response Pattern</h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.timeline.responsesByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date"
                      tickFormatter={formatDate}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={formatDate}
                      formatter={(value) => [`${value} responses`, 'Responses']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="responses" 
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Hourly Distribution */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Hourly Response Distribution
              </h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.timeline.responsesByHour}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="hour"
                      tickFormatter={(hour) => `${hour}:00`}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(hour) => `${hour}:00 - ${(hour + 1) % 24}:00`}
                      formatter={(value) => [`${value} responses`, 'Count']}
                    />
                    <Bar dataKey="count" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Demographics */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Demographics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Graduation Year */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-4">Graduation Year</h4>
                  {analytics.demographics?.byGraduationYear?.length > 0 ? (
                    <div className="space-y-4">
                      {analytics.demographics.byGraduationYear.map((item, index) => (
                        <div key={index} className="relative">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium">{item.label}</span>
                            <span className="text-gray-500">
                              {item.count} ({item.percentage}%)
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-2 bg-blue-500 rounded-full transition-all duration-500"
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <NoDataMessage message="No graduation year data available" />
                  )}
                </div>

                
              </div>
            </div>
          </div>
        );

      default:
        return <NoDataMessage message="Select a tab to view analytics" />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900 bg-opacity-50">
      <div className="h-full overflow-auto">
        <div className="min-h-screen px-4 text-center">
          <div className="inline-block w-full max-w-7xl my-8 text-left align-middle bg-gray-50 rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
              <div className="px-8 py-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Survey Analytics</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Detailed insights for {survey?.title || 'Untitled Survey'}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex items-center space-x-4 border-b border-gray-200">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px
                        ${activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      <tab.icon className="w-5 h-5" />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyAnalyticsModal;